/* ============================================
   SubirCotizacion — el documento ya está hecho, afuera

   De los tres caminos para cotizar desde el panel del partner, este es el que
   sirve hoy: el documento lo armó él en su herramienta y lo que falta es que
   viva en su CRM — que esté en el listado, cuente en los totales, tenga estado
   y un enlace para mandarle al cliente.

   El archivo se sube a `documento_versiones` y la cotización queda marcada
   `documento_subido`, así que las dos vistas que muestran el documento —el
   visor del panel y el enlace público— sirven el PDF en vez de renderizar el
   documento del ERP sobre campos vacíos.

   Dos campos y no diez: lo que el documento dice, lo dice el PDF. Acá solo va
   lo que el listado necesita para ordenar y sumar.
   ============================================ */
import { useEffect, useMemo, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import type { Cliente, Cotizacion, Lead } from '@/lib/types'
import { IconFileText, IconUpload, IconX } from '@/components/icons/Icons'

interface SubirCotizacionProps {
  /** Las empresas entre las que elegir. Ya vienen cargadas en quien abre esto. */
  cartera: Cliente[]
  /** Empresa y oportunidad preseleccionadas, cuando se abre desde una ficha. */
  clienteId?: number | null
  leadId?: number | null
  onCerrar: () => void
  onSubida: (cot: Cotizacion) => void
}

const MAX_MB = 20

export default function SubirCotizacion({
  cartera, clienteId: clienteInicial, leadId: leadInicial, onCerrar, onSubida,
}: SubirCotizacionProps) {
  const api = useApi()

  const [archivo, setArchivo] = useState<File | null>(null)
  const [cliente, setCliente] = useState(clienteInicial ? String(clienteInicial) : '')
  const [lead, setLead] = useState(leadInicial ? String(leadInicial) : '')
  const [nombre, setNombre] = useState('')
  const [total, setTotal] = useState('')
  const [moneda, setMoneda] = useState('PEN')
  const [estado, setEstado] = useState('enviada')
  const [subiendo, setSubiendo] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* Las oportunidades, para colgarle el documento a la que corresponda. Se
     piden al abrir y no antes: la mayoría de las veces el partner sube el
     documento desde la ficha, donde ya viene elegida. */
  const [leads, setLeads] = useState<Lead[]>([])
  useEffect(() => {
    let vivo = true
    api.get<Lead[]>('/api/leads')
      .then(r => { if (vivo) setLeads(Array.isArray(r) ? r : []) })
      .catch(() => { /* Sin la lista se sube igual: la oportunidad es opcional. */ })
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const leadsDeLaEmpresa = useMemo(() => {
    if (!cliente) return []
    return leads.filter(l => String(l.cliente_id ?? '') === cliente)
  }, [leads, cliente])

  function elegir(f: File | null) {
    setError(null)
    if (!f) { setArchivo(null); return }
    if (!/\.pdf$/i.test(f.name)) { setError('El documento tiene que ser un PDF'); return }
    if (f.size > MAX_MB * 1024 * 1024) { setError(`El PDF supera ${MAX_MB} MB`); return }
    setArchivo(f)
    // El nombre del archivo es casi siempre el nombre del trabajo.
    if (!nombre.trim()) setNombre(f.name.replace(/\.pdf$/i, ''))
  }

  async function subir() {
    if (!archivo) { setError('Elige el PDF'); return }
    if (!cliente) { setError('Elige la empresa'); return }
    setSubiendo(true)
    setError(null)
    try {
      const fd = new FormData()
      fd.append('file', archivo)
      fd.append('cliente_id', cliente)
      if (lead) fd.append('lead_id', lead)
      if (nombre.trim()) fd.append('proyecto_nombre', nombre.trim())
      if (total.trim()) fd.append('total', total.trim())
      fd.append('moneda', moneda)
      fd.append('estado', estado)
      const creada = await api.upload<Cotizacion>('/api/cotizaciones/subir', fd)
      onSubida(creada)
    } catch (err: any) {
      console.error('Error subiendo la cotización:', err)
      setError(err?.message || 'No se pudo subir el documento')
    } finally {
      setSubiendo(false)
    }
  }

  return (
    <div className="ld-modal-fondo" role="dialog" aria-modal="true" aria-label="Subir una cotización">
      <div className="ld-modal ld-modal--ancho">
        <h3 className="ld-modal__titulo">Subir una cotización</h3>
        <p className="ld-modal__texto">
          El PDF queda como el documento de esta cotización: es lo que se abre por el
          enlace que le compartes al cliente. Para corregirlo, subes uno nuevo y el
          anterior queda guardado.
        </p>

        <label className={`subir-cot__zona${archivo ? ' subir-cot__zona--lista' : ''}`}>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={e => elegir(e.target.files?.[0] ?? null)}
            className="subir-cot__input"
          />
          {archivo ? (
            <>
              <IconFileText size={20} />
              <span className="subir-cot__nombre">{archivo.name}</span>
              <span className="subir-cot__peso">{(archivo.size / 1024 / 1024).toFixed(1)} MB</span>
            </>
          ) : (
            <>
              <IconUpload size={20} />
              <span>Elige el PDF de la cotización</span>
            </>
          )}
        </label>

        <div className="subir-cot__grid">
          <div className="form-group">
            <label className="form-label">Empresa</label>
            <select
              className="form-input"
              value={cliente}
              onChange={e => { setCliente(e.target.value); setLead('') }}
            >
              <option value="">Elige la empresa...</option>
              {cartera.map(c => <option key={c.id} value={String(c.id)}>{c.empresa}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Oportunidad <span className="form-hint">opcional</span></label>
            <select
              className="form-input"
              value={lead}
              onChange={e => setLead(e.target.value)}
              disabled={!cliente}
            >
              <option value="">Sin oportunidad</option>
              {leadsDeLaEmpresa.map(l => (
                <option key={l.id} value={String(l.id)}>{l.codigo} — {l.titulo}</option>
              ))}
            </select>
          </div>

          <div className="form-group subir-cot__ancho">
            <label className="form-label">Qué se cotiza</label>
            <input
              className="form-input"
              value={nombre}
              onChange={e => setNombre(e.target.value)}
              placeholder="Rediseño de la web, Campaña de octubre..."
            />
          </div>

          <div className="form-group">
            <label className="form-label">Total <span className="form-hint">IGV incluido</span></label>
            <div className="subir-cot__monto">
              <select className="form-input" value={moneda} onChange={e => setMoneda(e.target.value)}>
                <option value="PEN">S/</option>
                <option value="USD">$</option>
                <option value="EUR">€</option>
              </select>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.01"
                value={total}
                onChange={e => setTotal(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Estado</label>
            <select className="form-input" value={estado} onChange={e => setEstado(e.target.value)}>
              <option value="enviada">Enviada</option>
              <option value="borrador">Borrador</option>
              <option value="en_negociacion">En negociación</option>
              <option value="aceptada">Aceptada</option>
              <option value="rechazada">Rechazada</option>
            </select>
          </div>
        </div>

        {error && <p className="ld-tareas__error">{error}</p>}

        <div className="ld-modal__acciones">
          <button type="button" className="btn btn-secondary" onClick={onCerrar} disabled={subiendo}>
            <IconX size={14} /> Cancelar
          </button>
          <button type="button" className="btn btn-primary" onClick={subir} disabled={subiendo}>
            {subiendo ? 'Subiendo...' : 'Subir la cotización'}
          </button>
        </div>
      </div>
    </div>
  )
}
